/**
 * Configuração centralizada da API
 * 
 * Uso:
 * - Defina REACT_APP_API_URL no .env (ex: http://3.236.26.254:8000)
 * - Este arquivo normaliza e adiciona /api/v1 automaticamente
 * - Todos os serviços devem usar getApiBaseUrl() ou API_BASE_URL
 */

/**
 * Obtém a URL base da API normalizada
 * - Remove trailing slash
 * - Adiciona /api/v1 se não estiver presente
 * - Retorna URL completa pronta para uso
 */
export function getApiBaseUrl(): string {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  
  if (!envUrl) {
    // Fallback: inferir do window.location
    if (typeof window !== 'undefined') {
      const { protocol, hostname } = window.location;
      return `${protocol}//${hostname}:8000/api/v1`;
    }
    return 'http://localhost:8000/api/v1';
  }
  
  // Remove trailing slash
  let normalizedUrl = envUrl.replace(/\/+$/, '');
  
  // Se não termina com /api/v1, adiciona
  if (!normalizedUrl.endsWith('/api/v1')) {
    // Se termina com /api, adiciona /v1
    if (normalizedUrl.endsWith('/api')) {
      normalizedUrl += '/v1';
    } else {
      // Adiciona /api/v1
      normalizedUrl += '/api/v1';
    }
  }
  
  return normalizedUrl;
}

/**
 * Obtém a URL base sem /api/v1 (útil para alguns casos específicos)
 */
export function getApiBaseUrlWithoutPath(): string {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  
  if (!envUrl) {
    if (typeof window !== 'undefined') {
      const { protocol, hostname } = window.location;
      return `${protocol}//${hostname}:8000`;
    }
    return 'http://localhost:8000';
  }
  
  // Remove trailing slash e /api/v1 se existir
  let normalizedUrl = envUrl.replace(/\/+$/, '');
  normalizedUrl = normalizedUrl.replace(/\/api\/v1\/?$/, '');
  
  return normalizedUrl;
}

/**
 * URL base da API (com /api/v1)
 * Use esta constante em todos os serviços
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * URL base sem /api/v1 (para casos específicos)
 */
export const API_BASE_URL_WITHOUT_PATH = getApiBaseUrlWithoutPath();

/**
 * URL base do Mestti OPS (/api/v1/ai).
 * Always derived from the configured API host (e.g. api.mestti.com.br), never the
 * Cloudflare Pages origin — production SPA and API live on different domains.
 * Dev: optional REACT_APP_AI_URL override.
 */
export function getAiBaseUrl(): string {
  const isProd = process.env.NODE_ENV === 'production';
  const override = (process.env.REACT_APP_AI_URL || '').trim();
  if (!isProd && override) {
    let base = override.replace(/\/+$/, '');
    if (!base.endsWith('/api/v1/ai')) {
      if (base.endsWith('/api/v1')) {
        base += '/ai';
      } else if (base.endsWith('/api')) {
        base += '/v1/ai';
      } else {
        base += '/api/v1/ai';
      }
    }
    return base;
  }
  return `${getApiBaseUrlWithoutPath()}/api/v1/ai`;
}

/**
 * Obtém a URL base do WebSocket baseada na configuração da API
 * - Converte http:// para ws://
 * - Converte https:// para wss://
 * - Remove /api/v1 se existir
 */
export function getWebSocketBaseUrl(): string {
  const apiUrl = getApiBaseUrlWithoutPath();
  
  // Converte protocolo HTTP para WebSocket
  if (apiUrl.startsWith('https://')) {
    return apiUrl.replace(/^https:\/\//, 'wss://');
  } else if (apiUrl.startsWith('http://')) {
    return apiUrl.replace(/^http:\/\//, 'ws://');
  }
  
  // Se não tem protocolo, assume ws:// (desenvolvimento)
  if (!apiUrl.startsWith('ws://') && !apiUrl.startsWith('wss://')) {
    return `ws://${apiUrl}`;
  }
  
  return apiUrl;
}

/**
 * Log da configuração (apenas em desenvolvimento)
 */
if (process.env.NODE_ENV === 'development') {
  // console.log('🔧 API Configuration:', {
  //   'REACT_APP_API_URL (env)': process.env.REACT_APP_API_URL || '(não definido)',
  //   'API_BASE_URL (final)': API_BASE_URL,
  //   'API_BASE_URL_WITHOUT_PATH': API_BASE_URL_WITHOUT_PATH,
  // });
}

